import _ from 'lodash';
import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import * as ExcelJS from 'exceljs/dist/exceljs';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { COLUMNS } from '../../enums/columns';
import { AuctionItemSheetColumns, PartySignupItemSheetColumns } from '../../enums/columns';
import { LiveAuctionPresetOptionSchema, SilentAuctionPresetOptionSchema } from 'app/campaign/structure/auction';
import { PartySignUpPresetOptionSchema } from 'app/campaign/structure/party-signup';
import * as moment from 'moment';

@Component({
  selector: 'app-import-xlsx',
  templateUrl: './import-xlsx.component.html',
  styleUrls: ['./import-xlsx.component.scss']
})
export class ImportXlsxComponent implements OnInit {

  @Input()
  public schema: any = null;

  @Input()
  public import: UntypedFormControl = null;

  @Input()
  public options: UntypedFormArray = null;

  @Output()
  public onChange: EventEmitter<any> = new EventEmitter();

  public extensions: string[] = ['xlsx'];

  public errors: string[] = [];
  private takeUntilSubject: Subject<any> = new Subject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private campaignService: CampaignService
  ) { }

  ngOnInit(): void {
    this.import.valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.errors = [];
    });

    this.import.valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe((file: File) => {
      this.onReadFile(file);
    });
  }

  getErrorMessaqge(control: FormControl, row, column) {
    let result = "";
    _.forEach(control.errors, (value, error)  => {
      if(value) {
        let errorMessage = `Row ${row+2}, Column "${COLUMNS[column]}".`;
        switch(error) {
          case 'required':
            if(column === 'buyItNowEnabled') {
              errorMessage += ` Value of column "${COLUMNS[column]}" cannot be 'No' if value of column '${COLUMNS['buyItNowOnlyItem']}' is 'Yes'.`;
            } else {
              errorMessage += ` Value of column "${COLUMNS[column]}" cannot be empty.`;
            }
            break;
          case 'min':
            errorMessage += ` Value of column "${COLUMNS[column]}" cannot be less than ${value.min}.`;
            break;
          case 'max':
            errorMessage += ` Value of column "${COLUMNS[column]}" cannot be greater than ${value.max}.`;
            break;
          case 'maxlength':
            errorMessage += ` Value of column "${COLUMNS[column]}" cannot be longer than ${value.requiredLength}.`;
            break;
          default:
            errorMessage += ' Unknown error.';
        }
        result = errorMessage;
      }
    })
    return result;
  }

  onReadFile(file: File) {
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      // read workbook and grab the first sheet
      const bstr: string = e.target.result;
      const workbook = new ExcelJS.Workbook();

      workbook.xlsx.load(bstr).then(() => {
        // Several headers for one key added for supporting of older versions xlsx templates 
        let itemsSheetColumns = null;
        let workSheetName = null;

        if(
          this.schema === SilentAuctionPresetOptionSchema ||
          this.schema === LiveAuctionPresetOptionSchema
        ) {
          itemsSheetColumns = AuctionItemSheetColumns;
          workSheetName = "Auction Items";
        }

        if(this.schema === PartySignUpPresetOptionSchema) {
          itemsSheetColumns = PartySignupItemSheetColumns;
          workSheetName = "Party Signup Items";
        }

        const workSheets = workbook.worksheets;
        let firstSheet = workbook.getWorksheet(workSheetName) || null;

        if(firstSheet === null && workSheets.length > 1) {
          this.errors = ['Multiple sheets are detected. Please delete extra sheets and try again.'];
          return;
        }

        if(firstSheet === null) {
          firstSheet = workSheets[0] || null;
        }

        if(firstSheet === null) {
          this.errors = ['Error reading the file. Please make sure it is a valid Excel file.'];
          return;
        }

        let data = [];
        let columns = [];
        let formDataRows = [];
        let isFirstRow = true;

        firstSheet.eachRow((rawRow) => {
          let row = []
          let value = null;

          // form the columns list by header name
          rawRow.eachCell({ includeEmpty: true }, function(cell, colNumber) {
            if(!isFirstRow) {
              return;
            }
            const value = cell.value || '';
            const filteredValue = value.replace(/\s+/g, ' '); // replace all spaces with one space
 
            const column = {};
            const itemSheetColumn = itemsSheetColumns.find(itemsSheetColumn => {
              if(itemsSheetColumn.formData) {
                const result = filteredValue.match(itemsSheetColumn.header);
                if(result === null) {
                  return false;
                }
                const [,index] = result;
                column["formDataIndex"] = parseInt(index);
                return true;
              }
              return itemsSheetColumn.header === filteredValue;
            })

            columns.push({...column, ...itemSheetColumn, colNumber});
          });

          if(isFirstRow) {
            formDataRows = _.chain(columns).filter(column => {
              return column.hasOwnProperty("formDataIndex");
            }).groupBy("formDataIndex").value();

            columns = _.chain(columns).filter(column => {
              return !column.hasOwnProperty("formDataIndex");
            }).value();

            isFirstRow = false;
          }

          // get the value of each cell
          rawRow.eachCell({ includeEmpty: true }, function(cell) {
            const cellValue = cell.value === 0 ? 0: cell.value || '';
            
            switch (cell.type) {
              case ExcelJS.ValueType.Number:
                const numFmt = cell.numFmt || "0.00";
                value = cellValue;

                if(numFmt[numFmt.length - 1] === "%") {
                  value = cellValue * 100;
                }
                break;

              case ExcelJS.ValueType.RichText:
                cellValue.richText.forEach(val => {
                  value += val.text;
                });
                break;

              case ExcelJS.ValueType.Hyperlink:
                value = cell.hyperlink
                break;

              default:
                value = cellValue;
                break;
            }

            row.push(value);
          });

          let values = _.map(row, (value, index) => {
            const str = new String(value);
            const column = columns.find((column) => {
              return column.colNumber === index + 1;
            }) || null;

            if(column === null) {
              return null;
            }

            return { 
              field: column.key,
              value: str.toString()
            };
          });

          let shoppingForm = _.map(formDataRows, formDataRow => {
            return _.map(formDataRow, formDataColumn => {
              return { 
                field: formDataColumn.key,
                value: row[formDataColumn.colNumber-1] || null
              };
            });
          });

          values = _.reject(values, value => value === null);
          shoppingForm = _.reject(shoppingForm, shoppingFormRow => {
            return !_.some(shoppingFormRow, (shoppingFormRowColumn) => {
              return shoppingFormRowColumn.value !== null;
            });
          });

          data.push([...values, {
            field: 'shoppingForm',
            value: shoppingForm
          }]);
        });

        data = _.map(data, row => {
          return _.map(row, cell => {
            const value = cell.value instanceof Array ? cell.value: _.trim(cell.value);
            return {...cell, value };
          });
        });
        data = _.filter(data, row => {
          for(let cell of row) {
            if(cell.value !== '') {
              return true;
            }
          }
          return false;
        });

        this.options = this.formBuilder.array([], []);
        this.errors = [];
        const result = this.campaignService.transformTo(data, this.schema);
        if(result.length < 1) {
          this.errors = ['Could not find Items'];
          return;
        }

        const list = [];
        if(
          this.schema === SilentAuctionPresetOptionSchema ||
          this.schema === LiveAuctionPresetOptionSchema
        ) {
          _.forEach(result, (obj, row) => {
            const option = this.formBuilder.group({});
            this.campaignService.generateForm(option, this.schema);
  
            const minBid = option.get('minBid');
            minBid.clearValidators();
  
            const bidIncrement = option.get('bidIncrement');
            bidIncrement.clearValidators();
  
            const buyItNowOnlyItem = option.get('buyItNowOnlyItem') || null;
            if(buyItNowOnlyItem) {
              buyItNowOnlyItem.valueChanges.pipe(
                filter(value => !!value),
                takeUntil(this.takeUntilSubject)
              ).subscribe(() => {
                const buyItNowEnabled = option.get('buyItNowEnabled');
                buyItNowEnabled.clearValidators();
                buyItNowEnabled.setValidators([Validators.requiredTrue]);
              });
            }
  
            if (obj.buyItNowOnlyItem) {
              minBid.setValidators([]);
              bidIncrement.setValidators([]);
  
              this.campaignService.configGroup(option, option, {
                name: 'buyItNowEnabled',
                value: true
              }, [
                { name: 'buyItNowAmount', defaultValue: 0, validators: [Validators.required, Validators.min(1)] },
              ], this.takeUntilSubject);
  
            } else {
              minBid.setValidators([Validators.required, Validators.min(1)]);
              bidIncrement.setValidators([Validators.required, Validators.min(1)]);
            }
  
            this.campaignService.configGroup(option, option, {
              name: 'collectSalesTax',
              value: true
            }, [
              { name: 'salesTax', defaultValue: 0, validators: [Validators.required, Validators.min(0.01), Validators.max(100)] },
            ], this.takeUntilSubject);
  
  
            const addInfoDesc = option.get('additionalInfoDesc');
            addInfoDesc.clearValidators();
  
            if(obj.additionalInfoRequired) {
              addInfoDesc.setValidators([Validators.required]);
  
              this.campaignService.configGroup(option, option, {
                name: 'additionalInfoRequired',
                value: true
              },[
                { name: 'additionalInfoDesc', defaultValue: "", validators: [Validators.required] },
                { name: 'additionalInfoDetail', defaultValue: "", validators: [Validators.required] }
              ], this.takeUntilSubject);
            } else {
              obj.additionalInfoDesc = "";
              obj.additionalInfoDetail = "";
            }
  
            const addInfoSelectDesc = option.get('additionalInfoSelectDesc');
            addInfoSelectDesc.clearValidators();
            const addInfoSelect = option.get('additionalInfoSelect');
            addInfoSelect.clearValidators();
  
            if(obj.additionalInfoSelectRequired) {
              addInfoSelectDesc.setValidators([Validators.required]);
              addInfoSelect.setValidators([Validators.required]);
  
              this.campaignService.configGroup(option, option, {
                name: 'additionalInfoSelectRequired',
                value: true
              },[
                { name: 'additionalInfoSelectDesc', defaultValue: "", validators: [Validators.required] },
                { name: 'additionalInfoSelect', defaultValue: [], validators: [Validators.required] }
              ], this.takeUntilSubject);
            } else {
              obj.additionalInfoSelectDesc = "";
              obj.additionalInfoSelect = [];
            }
  
            this.campaignService.initializeForm(option, obj, this.schema);
  
            _.forEach(option.controls, (control, column) => {
              if(control.invalid) {
                _.forEach(control.errors, (value, error)  => {
                  if(value) {
                    let errorMessage = `Row ${row+2}, Column "${COLUMNS[column]}".`;
                    switch(error) {
                      case 'required':
                        if(column === 'buyItNowEnabled') {
                          errorMessage += ` Value of column "${COLUMNS[column]}" cannot be 'No' if value of column '${COLUMNS['buyItNowOnlyItem']}' is 'Yes'.`;
                        } else {
                          errorMessage += ` Value of column "${COLUMNS[column]}" cannot be empty.`;
                        }
                        break;
                      case 'min':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be less than ${value.min}.`;
                        break;
                      case 'max':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be greater than ${value.max}.`;
                        break;
                      case 'maxlength':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be longer than ${value.requiredLength}.`;
                        break;
                      default:
                        errorMessage += ' Unknown error.';
                    }
                    this.errors.push(errorMessage);
                  }
                })
              }
            });
            this.options.push(option);
  
            list.push({
              ...this.campaignService.generateRequestObject(option, this.schema),
              id: row
            });
          });
        }

        if(this.schema === PartySignUpPresetOptionSchema) {
          _.forEach(result, (obj, row) => {
            const option = this.formBuilder.group({});
            this.campaignService.generateForm(option, this.schema);
            this.campaignService.initializeForm(option, obj, this.schema);

            const optionStartDate = option.get("optionStartDate");
            if(optionStartDate.value !== "") {
              const optionStartDateValue = moment(optionStartDate.value, "MM/DD/YYYY HH:mma").unix();
              if(_.isNaN(optionStartDateValue)) {
                optionStartDate.setErrors({
                  datetimeFormat: "MM/DD/YY HH:MM AM/PM"
                });
              } else {
                optionStartDate.setValue(optionStartDateValue);
              }
            } else {
              optionStartDate.setValue(0);
            }

            const optionEndDate = option.get("optionEndDate");
            if(optionEndDate.value !== "") {
              const optionEndDateValue = moment(optionEndDate.value, "MM/DD/YYYY HH:mma").unix();
              if(_.isNaN(optionEndDateValue)) {
                optionEndDate.setErrors({
                  datetimeFormat: "MM/DD/YY HH:MM AM/PM"
                });
              } else {
                optionEndDate.setValue(optionEndDateValue);
              }
            } else {
              optionEndDate.setValue(0);
            }

            const startDateTimeGmtUnix = option.get("startDateTimeGmtUnix");
            if(startDateTimeGmtUnix.value !== "" && startDateTimeGmtUnix.value !== 0) {
              const startDateTimeGmtUnixValue = moment(startDateTimeGmtUnix.value, "MM/DD/YYYY HH:mma").unix();
              if(_.isNaN(startDateTimeGmtUnixValue)) {
                startDateTimeGmtUnix.setErrors({
                  datetimeFormat: "MM/DD/YY HH:MM AM/PM"
                });
              } else {
                startDateTimeGmtUnix.setValue(startDateTimeGmtUnixValue);
              }
            }

            const eventVenue = option.get("eventVenue");
            const vEventVenueName = option.get("vEventVenueName");
            const vEventVenueAddress = option.get("vEventVenueAddress");
            const vEventVenueUnitNum = option.get("vEventVenueUnitNum");

            eventVenue.get("name").setValue(vEventVenueName.value);
            eventVenue.get("stringAddress").setValue(vEventVenueAddress.value);
            eventVenue.get("unitNum").setValue(vEventVenueUnitNum.value);

            const hostEmail = option.get("hostEmail");
            const hostEmailValue = hostEmail.value.indexOf("mailto:") === 0 ? hostEmail.value.replace("mailto:", ""): hostEmail.value;
            hostEmail.setValue(hostEmailValue);
  
            _.forEach(option.controls, (control, column) => {
              if(control.invalid) {
                if(control.errors === null) {
                  for(let i = 0; i < control.controls.length; i++) {
                    const formData = control.controls[i];
                    _.forEach(formData.controls, (control, formDataColumn) => {
                      _.forEach(control.errors, (value, error)  => {
                        let errorMessage = `Row ${row+2}, Column "${COLUMNS[column + "_" + formDataColumn]} ${i+1}".`;
                        this.errors.push(errorMessage);
                      });
                    })
                  }
                }
                _.forEach(control.errors, (value, error)  => {
                  if(value) {
                    let errorMessage = `Row ${row+2}, Column "${COLUMNS[column]}".`;
                    switch(error) {
                      case 'required':
                        if(column === 'notifyHost' || column === 'displayHostInfo') {
                          errorMessage += ` Value of column "${COLUMNS[column]}" cannot be 'Yes' if value of column '${COLUMNS['hostName']}' or '${COLUMNS['hostEmail']}' is empty.`;
                        } else if(column === 'hideInventory') {
                          errorMessage += ` Value of column "${COLUMNS[column]}" cannot be 'No' if value of column '${COLUMNS['enforceInventory']}' is 'No'.`;
                        } else {
                          errorMessage += ` Value of column "${COLUMNS[column]}" cannot be empty.`;
                        }
                        break;
                      case 'min':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be less than ${value.min}.`;
                        break;
                      case 'max':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be greater than ${value.max}.`;
                        if(column === 'inventory' && value.max === 0) {
                          errorMessage += ` Enable "${COLUMNS['enforceInventory']}" if you want to change value of "${COLUMNS[column]}".`
                        }
                        break;
                      case 'maxlength':
                        errorMessage += ` Value of column "${COLUMNS[column]}" cannot be longer than ${value.requiredLength}.`;
                        break;
                      case 'url':
                        errorMessage += ` Value of column "${COLUMNS[column]}" has invalid link.`;
                        break;
                      case 'datetimeFormat':
                        errorMessage += ` Value of column "${COLUMNS[column]}" has incorrect format. Try to use "${value}" format.`;
                        break;
                      default:
                        errorMessage += ' Unknown error.';
                    }
                    this.errors.push(errorMessage);
                  }
                })
              }
            });
            this.options.push(option);
  
            list.push({
              ...this.campaignService.generateRequestObject(option, this.schema),
              id: row
            });
          });
        }

        if(this.errors.length < 1) {
          this.onChange.next(this.options);
        }
      });
    };
    reader.readAsBinaryString(file);
  }

  onSelect(file: File) {
    this.import.setValue(file);
  }

}
